import { useState, useEffect, useCallback, useContext } from 'react'
import { useParams, useNavigate } from "react-router";
import { useMutation, useLazyQuery } from "@apollo/client";
import { UPDATE_PUBLISHER, UPDATE_CATALOG } from "../../graphql/mutations";
import { CATALOG_DETAILS } from "../../graphql/queries";
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext';
import { useBreadcrumbs } from "../../contexts/BreadcrumbProvider"

export const useCreateCatalog = () => {
    const {user, publisher} = useContext(PublisherAndUserContext);
    const [staff, setStaff] = useState([]);
    const [buttonText, setButtonText] = useState("Create");
    const [catalogAdmins, setCatalogAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [catalog, setCatalog] = useState({
        catalogName: "Untitled Catalog",
        heading: "",
        subHeading: "",
        description: "",
        catalogAdmins: {
            connect: [],
        },
    });
    const { id: catalog_id } = useParams();
    const navigate = useNavigate();
    const { addBreadcrumb } = useBreadcrumbs();

    const [createCatalog] = useMutation(UPDATE_PUBLISHER, {
        onCompleted: (data) => {
            setCatalog(data.updatePublisher.catalogs[0]);
            setLoading(false)
            addBreadcrumb("Catalogs", "/catalogs", true)
            navigate("/catalogs");
        },
        onError: () => {
            setLoading(false)
        },
    });
    const [updateCatalog] = useMutation(UPDATE_CATALOG, {
        onCompleted: (data) => {
            setCatalog(data.updateCatalog);
            setLoading(false)
            navigate("/catalogs");
        },
        onError: () => {
            setLoading(false)
        },
    });
    const [getCatalog] = useLazyQuery(CATALOG_DETAILS, {
        onCompleted: (data) => {
            setCatalog(data.catalog);
            setCatalogAdmins(data.catalog.catalogAdmins.map((user) => ({
                ...user,
                name: `${user.firstName} ${user.lastName}`,
                disabled: publisher?.owner?.id === user.id,
            })));
            setLoading(false)
        },
        onError: () => {
            setLoading(false)
        },
    });

    useEffect(() => {
        if (catalog_id) {
            setButtonText("Update");
            setLoading(true)
            if(publisher?.id) {
                getCatalog({
                    variables: {
                        where: {
                            id: catalog_id,
                        },
                    },
                });
            }
        } else {
            setButtonText("Create");
            setCatalog({
                catalogName: "Untitled Catalog",
                heading: "",
                subHeading: "",
                description: "",
                catalogAdmins: {
                    connect: [],
                },
            });
        }
    }, [catalog_id, getCatalog, publisher.id]);

    useEffect(() => {
        if (publisher && publisher.id && staff.length === 0) {
            setStaff(
                [
                    ...(publisher.additionalAdmins ? publisher.additionalAdmins : []),
                    ...(publisher.staff ? publisher.staff : []),
                    publisher.owner,
                ].map((user) => ({
                    ...user,
                    name: `${user.firstName} ${user.lastName}`,
                }))
            );
            if (window.location.href.includes("createcatalog")) {
                setCatalogAdmins([
                    ...catalogAdmins,
                    {
                        ...publisher.owner,
                        name: `${publisher.owner.firstName} ${publisher.owner.lastName}`,
                        disabled: true,
                    },
                ]);
            }
        }
    }, [publisher, catalogAdmins, staff]);

    const checklistCallback = useCallback((list) => {
        setCatalogAdmins(list);
    }, []);
    const onChange = (event) => {
        setCatalog({ ...catalog, [event.target.name]: event.target.value });
    };
    const catalogSubmit = (e) => {
        e.preventDefault();
        if (catalog?.id) {
            setLoading(true)
            updateCatalog({
                variables: {
                    where: {
                        id: catalog.id,
                    },
                    data: {
                        catalogName: { set: catalog.catalogName },
                        heading: { set: catalog.heading },
                        subHeading: { set: catalog.subHeading },
                        description: { set: catalog.description },
                        catalogAdmins: {
                            connect: catalogAdmins.map((user) => ({ id: user.id })),
                        },
                    },
                },
            });
        } else {
            const index =
                publisher.catalogs?.length > 0 ? publisher.catalogs[0].index + 1 : 1;
            setLoading(true)
            createCatalog({
                variables: {
                    data: {
                        catalogs: {
                            create: [
                                {
                                    ...catalog,
                                    index: index,
                                    catalogAdmins: {
                                        connect: catalogAdmins.map((user) => ({ id: user.id })),
                                    },
                                },
                            ],
                        },
                    },
                    where: {
                        id: publisher.id,
                    },
                },
            });
        }
    };

    return {
        catalog,
        catalogSubmit,
        staff,
        onChange,
        catalogAdmins,
        checklistCallback,
        buttonText,
        createCatalog,
        setStaff,
        loading,
        user
    }
}